<template>
  <div>
    <!-- Passivo no topo, não escondido num relatório: o saldo de todos é dinheiro
         que a Hubee deve, e passivo que ninguém mede vira surpresa de caixa. -->
    <div class="vx-row mb-2">
      <div class="vx-col sm:w-1/2 w-full mb-4">
        <vx-card>
          <span class="label">{{ $t('wallet_admin.liability') }}</span>
          <span class="value">{{ money(liabilityCents) }}</span>
          <small class="text-muted">{{ $t('wallet_admin.liability_hint') }}</small>
        </vx-card>
      </div>
      <div class="vx-col sm:w-1/2 w-full mb-4">
        <vx-card>
          <span class="label">{{ $t('wallet_admin.negative') }}</span>
          <span class="value" :class="negativeWallets.length ? 'text-danger' : ''">
            {{ negativeWallets.length }}
          </span>
          <small class="text-muted">{{ $t('wallet_admin.negative_hint') }}</small>
        </vx-card>
      </div>
    </div>

    <vx-card>
      <h4 class="mb-2">{{ $t('wallet_admin.title') }}</h4>

      <div v-if="isLoading" class="text-center py-4 text-muted">{{ $t('common.loading') }}</div>
      <div v-else-if="!rows.length" class="text-muted">{{ $t('wallet_admin.empty') }}</div>

      <table v-else class="w-full admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{{ $t('wallet_admin.owner') }}</th>
            <th class="text-right">{{ $t('influencer_panel.pending') }}</th>
            <th class="text-right">{{ $t('influencer_panel.spendable') }}</th>
            <th class="text-right">{{ $t('influencer_panel.withdrawable') }}</th>
            <th>{{ $t('status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="w in rows" :key="w.id">
            <td>{{ w.id }}</td>
            <td>{{ _.get(w, 'influencer.name') || `#${w.owner_id}` }}</td>
            <td class="text-right text-warning">{{ money(_.get(w, 'balances.pending')) }}</td>
            <td class="text-right text-success">{{ money(_.get(w, 'balances.spendable')) }}</td>
            <td class="text-right text-primary">{{ money(_.get(w, 'balances.withdrawable')) }}</td>
            <td>
              <vs-chip transparent :color="w.status === 'AC' ? 'success' : 'danger'">
                {{ w.status === 'AC' ? $t('active') : $t('wallet_admin.blocked') }}
              </vs-chip>
            </td>
            <td class="text-right actions">
              <b-button size="sm" variant="outline-primary" @click="openDetail(w)">
                {{ $t('wallet_admin.statement') }}
              </b-button>
              <b-button v-permission="'wallets.adjust'" size="sm" variant="outline-warning" @click="openAdjust(w)">
                {{ $t('wallet_admin.adjust') }}
              </b-button>
              <b-button
                v-permission="'wallets.block'"
                size="sm"
                :variant="w.status === 'AC' ? 'outline-danger' : 'outline-success'"
                @click="toggleBlock(w)">
                {{ w.status === 'AC' ? $t('wallet_admin.block') : $t('wallet_admin.unblock') }}
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </vx-card>

    <b-modal v-model="detailModal" :title="$t('wallet_admin.statement')" size="lg" ok-only :ok-title="$t('close')">
      <div v-if="detail">
        <div class="stats-row mb-2">
          <div class="stat">
            <span class="stat-label">{{ $t('influencer_panel.pending') }}</span>
            <span class="stat-value">{{ money(detail.balances.pending) }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">{{ $t('influencer_panel.spendable') }}</span>
            <span class="stat-value">{{ money(detail.balances.spendable) }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">{{ $t('influencer_panel.withdrawable') }}</span>
            <span class="stat-value">{{ money(detail.balances.withdrawable) }}</span>
          </div>
        </div>
        <table class="w-full admin-table">
          <tbody>
            <tr v-for="e in detail.entries" :key="e.id">
              <td>{{ formatDateTime(e.created_at) }}</td>
              <td>
                {{ e.type_label }}
                <small v-if="e.description" class="text-muted d-block">{{ e.description }}</small>
              </td>
              <td class="text-right" :class="e.signed_amount_cents < 0 ? 'text-danger' : 'text-success'">
                {{ money(e.signed_amount_cents) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <b-modal
      v-model="adjustModal"
      :title="$t('wallet_admin.adjust')"
      :ok-title="$t('save')"
      :cancel-title="$t('action.cancel')"
      @ok="confirmAdjust">
      <b-form-group :label="$t('wallet_admin.adjust_amount')">
        <b-input-group prepend="R$">
          <b-form-input v-model="adjustAmount" type="number" step="0.01"/>
        </b-input-group>
        <small class="text-muted">{{ $t('wallet_admin.adjust_hint') }}</small>
      </b-form-group>
      <b-form-group :label="$t('wallet_admin.adjust_reason')">
        <b-form-textarea v-model="adjustReason" rows="2"/>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BModal, BFormGroup, BFormInput, BFormTextarea, BInputGroup
} from 'bootstrap-vue'
import WalletAdminService from '@/services/api/WalletAdminService'

export default {
  components: { BButton, BModal, BFormGroup, BFormInput, BFormTextarea, BInputGroup },
  data: () => ({
    service: null,
    rows: [],
    isLoading: false,
    liabilityCents: 0,
    negativeWallets: [],
    detailModal: false,
    detail: null,
    adjustModal: false,
    adjustAmount: null,
    adjustReason: '',
    current: null
  }),
  methods: {
    money(cents) {
      return (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatDateTime(v) { return v ? new Date(v).toLocaleString('pt-BR') : '' },
    load() {
      this.isLoading = true
      this.service.wallets().then(
        r => { this.rows = r.data || [] },
        () => { this.rows = [] }
      ).finally(() => { this.isLoading = false })

      this.service.liability().then(
        r => {
          this.liabilityCents = r.liability_cents || 0
          this.negativeWallets = r.negative_wallets || []
        },
        () => {}
      )
    },
    openDetail(wallet) {
      this.$vs.loading()
      this.service.walletDetail(wallet.id).then(
        d => { this.detail = d; this.detailModal = true },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    openAdjust(wallet) {
      this.current = wallet
      this.adjustAmount = null
      this.adjustReason = ''
      this.adjustModal = true
    },
    confirmAdjust() {
      if (!this.current || !this.adjustAmount || !this.adjustReason) return
      // Valor negativo debita, positivo credita — a régua é a mesma do serviço.
      const cents = Math.round(Number(this.adjustAmount) * 100)
      this.$vs.loading()
      this.service.adjust(this.current.id, cents, this.adjustReason).then(
        () => { this.notifySuccess(this.$vs, this.$t('messages.success')); this.load() },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    toggleBlock(wallet) {
      const isBlocking = wallet.status === 'AC'
      const reason = isBlocking ? window.prompt(this.$t('wallet_admin.block_reason')) : ''
      if (isBlocking && reason === null) return

      this.$vs.loading()
      const call = isBlocking
        ? this.service.blockWallet(wallet.id, reason || '')
        : this.service.unblockWallet(wallet.id)

      call.then(
        () => { this.notifySuccess(this.$vs, this.$t('messages.success')); this.load() },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    }
  },
  beforeMount() { this.service = WalletAdminService.build(this.$vs) },
  mounted() { this.load() }
}
</script>

<style scoped>
.label { font-size: 0.8rem; opacity: 0.7; display: block; }
.value { font-size: 1.6rem; font-weight: 700; display: block; line-height: 1.2; }
.admin-table th { font-size: 0.75rem; opacity: 0.7; text-align: left; padding-bottom: 0.35rem; }
.admin-table td { padding: 0.55rem 0.4rem; border-top: 1px solid rgba(0,0,0,0.06); vertical-align: top; }
.text-right { text-align: right; }
.actions { white-space: nowrap; }
.actions .btn { margin-left: 0.25rem; }
.stats-row { display: flex; gap: 2rem; }
.stat { display: flex; flex-direction: column; }
.stat-label { font-size: 0.75rem; opacity: 0.7; }
.stat-value { font-size: 1.1rem; font-weight: 600; }
</style>
